import { tokens } from '@kdk/shared/theme';
import { Box } from '@mui/material';
import type { ReactNode, RefObject, UIEventHandler } from 'react';
import { useRef } from 'react';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import { SIDEBAR_SCROLLBAR_COLORS, simplebarHeightChainSx, simplebarLookSx } from './simplebarSx';

type Axis = 'x' | 'y' | 'both';
type Breakpoint = 'xs' | 'sm' | 'md';

type Props = {
  /** スクロールする領域の名前(キーボードで操作できる領域として読み上げられる)。必須。 */
  ariaLabel: string;
  /** スクロールする向き。既定は縦横とも。 */
  axis?: Axis;
  /**
   * true なら、flex の縦の列(`fillPageSx` など)の中で、残りの高さをすべて受け取る。
   * 親の側が `minHeight: 0` を持つ flex 列であること。`maxHeight` とは同時に使わない。
   */
  fill?: boolean;
  /** 高さの上限。これを超えると領域の中だけが縦にスクロールする(複数の塊があるページ向け)。 */
  maxHeight?: string | number;
  /** `fill` / `maxHeight` を効かせる最小の画面幅。これより狭い画面では縦に伸ばし、横スクロールだけにする。 */
  verticalFrom?: Breakpoint;
  /** 'card' は枠線と背景のあるカード。'plain' は枠なし(ドロワーの中など)。 */
  variant?: 'card' | 'plain';
  /** 'sidebar' は濃い背景(サイドバー)用のスクロールバーの色。既定は表の色。 */
  tone?: 'default' | 'sidebar';
  /** 実際にスクロールするノード(`.simplebar-content-wrapper`)の ref。ドラッグ操作などで scrollLeft を触る用。 */
  scrollRef?: RefObject<HTMLDivElement | null>;
  onScroll?: UIEventHandler<HTMLDivElement>;
  /** 実際にスクロールするノードに付ける data-testid(テスト用)。 */
  scrollTestId?: string;
  children: ReactNode;
};

/** `from` 以上の幅でだけ `active` を、それより狭い幅では `base` を使う値(sx のブレークポイント指定)。 */
const fromBreakpoint = <T extends string | number>(from: Breakpoint, base: T, active: T) =>
  from === 'xs' ? active : { xs: base, [from]: active };

/**
 * すべての表に使うスクロール枠。SimpleBar で、常時表示のカプセル形スクロールバーを出す。
 * 見出し行・先頭列の `position: sticky` は、この枠の中のスクロールを基準に効く。
 *
 * - `fill`: 残りの高さを受け取る(1 つの表だけのページ)。
 * - `maxHeight`: 高さを上限で抑える(複数の塊があるページ)。
 * - どちらも `verticalFrom` より狭い画面では効かず、横スクロールだけになる。
 */
export const TableScroll = ({
  ariaLabel,
  axis = 'both',
  fill = false,
  maxHeight,
  verticalFrom = 'sm',
  variant = 'card',
  tone = 'default',
  scrollRef,
  onScroll,
  scrollTestId,
  children,
}: Props) => {
  const ownRef = useRef<HTMLDivElement>(null);
  const horizontal = axis !== 'y';
  const vertical = axis !== 'x';
  const capped = fill || maxHeight !== undefined;

  const scrollNodeProps = {
    ref: scrollRef ?? ownRef,
    onScroll,
    ...(scrollTestId === undefined ? {} : { 'data-testid': scrollTestId }),
  };

  return (
    <Box
      sx={[
        {
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          minHeight: 0,
          maxWidth: '100%',
          overflow: 'hidden',
          flex: fill ? fromBreakpoint(verticalFrom, 'none', '1 1 0') : 'none',
          '& [data-simplebar]': {
            minHeight: 0,
            width: '100%',
            ...(fill && { height: fromBreakpoint(verticalFrom, 'auto', '100%') }),
            ...(maxHeight !== undefined && {
              maxHeight: fromBreakpoint(verticalFrom, 'none', maxHeight),
            }),
          },
        },
        variant === 'card' && {
          bgcolor: 'background.paper',
          border: `1px solid ${tokens.line}`,
          borderRadius: 1,
        },
        simplebarLookSx(
          horizontal,
          vertical,
          tone === 'sidebar' ? SIDEBAR_SCROLLBAR_COLORS : undefined,
        ),
        fill && simplebarHeightChainSx,
        // 縦のスクロールバーは、縦に抑える画面幅でだけ出す
        capped &&
          vertical && {
            '& .simplebar-track.simplebar-vertical': {
              display: fromBreakpoint(verticalFrom, 'none', 'block'),
            },
          },
      ]}
    >
      <SimpleBar autoHide={false} ariaLabel={ariaLabel} scrollableNodeProps={scrollNodeProps}>
        {children}
      </SimpleBar>
    </Box>
  );
};
